import { isValidSharpCharts2DateString } from '@/utils/isValidSharpCharts2DateString';
import { isValidTimeString } from '@/utils/isValidTimeString';
import { removeDuplicatesFromArray } from '@/utils/removeDuplicatesFromArray';
import { addMinutesToDateTimeString } from '@/utils/addMinutesToDateTimeString';
import { PromiseCacheMap, TimeCacheMap } from '@/utils/cache-map';

const ONE_MINUTE = 60 * 1000;
const MAX_CACHE_ENTRY_AGE = 5 * ONE_MINUTE;
const REMOVE_OLD_CACHE_ENTRIES_LOOP_FREQUENCY = 1 * ONE_MINUTE;
const DEFAULT_IS_EXCHANGE_OPEN_VALUE = false;

const regularHoursIsExchangeOpenPromisesCache = new PromiseCacheMap<boolean>();
const extendedHoursIsExchangeOpenPromisesCache = new PromiseCacheMap<boolean>();
const isMarketOpenByExchangeRegularHoursCache = new TimeCacheMap<boolean>(REMOVE_OLD_CACHE_ENTRIES_LOOP_FREQUENCY, MAX_CACHE_ENTRY_AGE);
const isMarketOpenByExchangeExtendedHoursCache = new TimeCacheMap<boolean>(REMOVE_OLD_CACHE_ENTRIES_LOOP_FREQUENCY, MAX_CACHE_ENTRY_AGE);

interface IsMarketOpenResponse {
  date: string
  ismarketopen: boolean
}

function isIsMarketOpenResponse(unknownValue: unknown): unknownValue is IsMarketOpenResponse {
  return (
    typeof unknownValue === 'object' &&
    unknownValue !== null &&
    'date' in unknownValue &&
    'ismarketopen' in unknownValue &&
    typeof unknownValue.date === 'string' &&
    typeof unknownValue.ismarketopen === 'boolean'
  );
}

async function fetchIsMarketOpen(exchange: string, dateTimeString?: string): Promise<IsMarketOpenResponse> {
  const urlSearchParams = new URLSearchParams({
    e: exchange,
    randomNumber: Date.now().toString()
  });

  let responseJson;

  try {
    if (typeof dateTimeString === 'string' && dateTimeString.replace(/\s/g, '')) {
      const dateTimeStringParts = dateTimeString
        .replace(/-/g, '')
        .replace(/:/g, '')
        .split(/\s/)
        .filter(dateTimeStringPart => !!dateTimeStringPart);
      const [date, time] = dateTimeStringParts;

      urlSearchParams.set('d', date);
      urlSearchParams.set('t', time);
    }

    const response = await fetch(`/quotebrain/ismarketopen?${urlSearchParams.toString()}`, {
      method: 'GET'
    });

    responseJson = await response.json();

    window.sc3WorkbenchLogger.info(`is-market-open responseJson for exchange: ${typeof exchange} ${exchange} and dateTimeString: ${typeof dateTimeString} ${dateTimeString}.`, responseJson);
  } catch (e) {
    window.sc3WorkbenchLogger.error(`Failed to fetch is-market-open for exchange: ${typeof exchange} ${exchange} and dateTimeString: ${typeof dateTimeString} ${dateTimeString}.`, e);

    throw new Error(`Failed to fetch is-market-open for exchange: ${typeof exchange} ${exchange} and dateTimeString: ${typeof dateTimeString} ${dateTimeString}.`, { cause: e });
  }

  if (!isIsMarketOpenResponse(responseJson)) {
    throw new Error(`Failed to fetch is-market-open for exchange: ${typeof exchange} ${exchange} and dateTimeString: ${typeof dateTimeString} ${dateTimeString} - responseJson does not match IsMarketOpenResponse interface: ${typeof responseJson} ${responseJson}.`);
  }

  if (!responseJson.date.trim()) {
    throw new Error(`Failed to fetch is-market-open for exchange: ${typeof exchange} ${exchange} and dateTimeString: ${typeof dateTimeString} ${dateTimeString} - trimmed responseJson.date is an empty string.`);
  }

  return responseJson;
}

async function _isExchangeOpen(exchange: string, considerExtendedHours: boolean, isRetrying?: boolean): Promise<boolean> {
  if (isMarketOpenByExchangeRegularHoursCache.get(exchange, false)) {
    return true;
  }

  if (isMarketOpenByExchangeExtendedHoursCache.get(exchange, false) === false) {
    return false;
  }

  if (!considerExtendedHours && isMarketOpenByExchangeRegularHoursCache.has(exchange)){
    return isMarketOpenByExchangeRegularHoursCache.get(exchange, false)!;
  }

  if (considerExtendedHours && isMarketOpenByExchangeExtendedHoursCache.has(exchange)) {
    return isMarketOpenByExchangeExtendedHoursCache.get(exchange, false)!;
  }

  try {
    const rawExchangeInfo = await fetchIsMarketOpen(exchange);

    if (rawExchangeInfo.ismarketopen) {
      isMarketOpenByExchangeRegularHoursCache.set(exchange, true);
      isMarketOpenByExchangeExtendedHoursCache.set(exchange, true);
      return true;
    }

    const currentDateTimeString = rawExchangeInfo.date.trim();
    const [dateString, time] = currentDateTimeString.split(/\s/).filter(currentDateTimeStringPart => !!currentDateTimeStringPart);

    if (!isValidSharpCharts2DateString(dateString) || !isValidTimeString(time)) {
      throw new Error(`Unable to parse rawExchangeInfo.date: ${currentDateTimeString} while fetching is-market-open (isRetrying: ${isRetrying}) for exchange: ${typeof exchange} ${exchange}.`);
    }

    const fifteenMinutesFromNow = addMinutesToDateTimeString(currentDateTimeString, 15);
    const thirtyFiveMinutesAgo = addMinutesToDateTimeString(currentDateTimeString, -35);

    if (!considerExtendedHours) {
      const allPromises = await Promise.all([
        fetchIsMarketOpen(exchange, fifteenMinutesFromNow),
        fetchIsMarketOpen(exchange, thirtyFiveMinutesAgo)
      ]);

      const [
        willRegularMarketBeOpenInTenMinutesResponse,
        wereRegularMarketHoursOpenThirtyFiveMinutesAgoResponse
      ] = allPromises;

      const willRegularMarketHoursBeOpenInTenMinutes = willRegularMarketBeOpenInTenMinutesResponse.ismarketopen;
      const wereRegularMarketHoursOpenThirtyFiveMinutesAgo = wereRegularMarketHoursOpenThirtyFiveMinutesAgoResponse.ismarketopen;
      const isMarketOpen = willRegularMarketHoursBeOpenInTenMinutes || wereRegularMarketHoursOpenThirtyFiveMinutesAgo;

      isMarketOpenByExchangeRegularHoursCache.set(exchange, isMarketOpen);

      if (isMarketOpen) {
        isMarketOpenByExchangeExtendedHoursCache.set(exchange, isMarketOpen);
      }

      return isMarketOpen;
    }

    const fifteenMinutesPlusTotalMinutesInPreMarketSessionFromNow = addMinutesToDateTimeString(fifteenMinutesFromNow, 330);
    const thirtyFiveMinutesPlusTotalMinutesInPostMarketSessionAgo = addMinutesToDateTimeString(thirtyFiveMinutesAgo, -240);

    const allPromises = await Promise.all([
      fetchIsMarketOpen(exchange, fifteenMinutesFromNow),
      fetchIsMarketOpen(exchange, fifteenMinutesPlusTotalMinutesInPreMarketSessionFromNow),
      fetchIsMarketOpen(exchange, thirtyFiveMinutesAgo),
      fetchIsMarketOpen(exchange, thirtyFiveMinutesPlusTotalMinutesInPostMarketSessionAgo)
    ]);

    const [
      willRegularMarketHoursBeOpenInTenMinutesResponse,
      willExtendedMarketHoursBeOpenInTenMinutesResponse,
      wereRegularMarketHoursOpenThirtyFiveMinutesAgoResponse,
      wereExtendedMarketHoursOpenThirtyFiveMinutesAgoResponse
    ] = allPromises;

    const willRegularMarketHoursBeOpenInTenMinutes = willRegularMarketHoursBeOpenInTenMinutesResponse.ismarketopen;
    const willExtendedMarketHoursBeOpenInTenMinutes = willExtendedMarketHoursBeOpenInTenMinutesResponse.ismarketopen;
    const wereRegularMarketHoursOpenThirtyFiveMinutesAgo = wereRegularMarketHoursOpenThirtyFiveMinutesAgoResponse.ismarketopen;
    const wereExtendedMarketHoursOpenThirtyFiveMinutesAgo = wereExtendedMarketHoursOpenThirtyFiveMinutesAgoResponse.ismarketopen;
    const isMarketOpen = willRegularMarketHoursBeOpenInTenMinutes || willExtendedMarketHoursBeOpenInTenMinutes || wereRegularMarketHoursOpenThirtyFiveMinutesAgo || wereExtendedMarketHoursOpenThirtyFiveMinutesAgo;

    isMarketOpenByExchangeExtendedHoursCache.set(exchange, isMarketOpen);

    if (!isMarketOpen) {
      isMarketOpenByExchangeRegularHoursCache.set(exchange, isMarketOpen);
    }

    return isMarketOpen;
  } catch (e) {
    if (isRetrying) {
      window.sc3WorkbenchLogger.error(`Error while checking if exchange is considered open while retrying, returning ${DEFAULT_IS_EXCHANGE_OPEN_VALUE}.`, e);
      return DEFAULT_IS_EXCHANGE_OPEN_VALUE;
    }

    return await _isExchangeOpen(exchange, considerExtendedHours, true);
  }
}

async function isExchangeOpen(exchange: string, considerExtendedHours: boolean) {
  if (typeof exchange !== 'string') { // TODO: this will be left in until the using file can be converted to TS
    return DEFAULT_IS_EXCHANGE_OPEN_VALUE;
  }

  let exchanges = exchange
    .replace(/\s/g, '')
    .toUpperCase()
    .split(/[:\-\\/]/)
    .filter(e => !!e);
  exchanges = removeDuplicatesFromArray(exchanges);

  for (const individualExchange of exchanges) {
    const isExchangeOpenPromisesCache = considerExtendedHours ? extendedHoursIsExchangeOpenPromisesCache : regularHoursIsExchangeOpenPromisesCache;
    let promiseObject = isExchangeOpenPromisesCache.get(individualExchange, true);

    if (!promiseObject) {
      promiseObject = _isExchangeOpen(individualExchange, considerExtendedHours);

      isExchangeOpenPromisesCache.set(individualExchange, promiseObject);
    }

    const isOpen = await promiseObject;

    if (isOpen) {
      return isOpen;
    }
  }

  return false;
}

export function useExchangeHours() {
  return {
    isExchangeOpen
  };
}
